import cn from 'classnames';
import React, { useContext } from 'react';

import { ToothStatus } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';
import {
  getDisplayToothNumber,
  isThirdMolarTooth,
  toothModel,
} from '@/entities/tooth';

import {
  ToothChartItemClickHandler,
  ToothChartSize,
  ToothItem,
} from '../../config/types';
import { ToothChartIcon } from '../ToothChartIcon/ToothChartIcon';
import { ToothChartContext } from '../ToothChartRoot/ToothChartRoot';
import { ToothItemStripes } from '../ToothItemStripes/ToothItemStripes';

import styles from './ToothChartItem.module.scss';

const sizeStyles: Record<ToothChartSize, string> = {
  default: styles.sizeDefault,
  small: styles.sizeSmall,
  mobile: styles.sizeMobile,
};

const statusStyles: Record<ToothItem['status'], string> = {
  healthy: styles.statusHealthy,
  unhealthy: styles.statusUnhealthy,
  missing: styles.statusMissing,
  treated: styles.statusTreated,
  lowProbability: styles.statusLowProbability,
  notFound: styles.statusNotFound,
};

type ToothChartItemProps = ToothItem & {
  className?: string;
  blackAndWhite?: boolean;
  preview?: boolean;
  supernumeraryIndex?: number;
  onClick?: ToothChartItemClickHandler;
};

const InternalToothChartItem: React.ForwardRefRenderFunction<
  HTMLDivElement,
  ToothChartItemProps
> = (props, ref) => {
  const {
    className,
    id,
    status,
    isLowerJaw,
    ISONumber,
    active,
    blackAndWhite = false,
    preview = false,
    childItems = [],
    supernumeraryIndex,
    gridArea,
    onClick,
    isApproved,
    isRemoved = false,
    ...rest
  } = props;

  const size = useContext(ToothChartContext);

  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );
  const customMode = useAppSelector(
    toothModel.selectors.selectToothChartCustomMode,
  );
  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );
  const { isFDA, isFDA3DGPRestrictions, isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const isSuper = childItems.length > 0;

  const focused =
    !preview &&
    (activeToothID === id ||
      childItems.some((item) => item.id === activeToothID));

  const isThirdMolar = isThirdMolarTooth(ISONumber);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick && status !== ToothStatus.notFound) {
      event.stopPropagation();
      onClick({ id, active, ISONumber });
    }
  };

  return (
    <div
      ref={ref}
      style={{ gridArea: `t${gridArea}` }}
      className={cn(
        className,
        styles.container,
        sizeStyles[size],
        !customMode && statusStyles[status],
        !active && styles.inactive,
        !active && customMode && styles.customModeInactive,
        focused && styles.focused,
        isLowerJaw && styles.lowerJaw,
        isThirdMolar && styles.thirdMolar,
        blackAndWhite && styles.blackAndWhite,
        isFDA && styles.fda,
        isApproved && !isFDA3DGPRestrictions && styles.approved,
        preview && styles.preview,
      )}
      data-component="tooth-chart-item"
      onClick={handleClick}
      {...rest}
    >
      {!isFDAVersion2 && (
        <ToothItemStripes
          toothID={id}
          isLowerJaw={isLowerJaw}
          blackAndWhite={blackAndWhite}
        />
      )}

      {isSuper ? (
        <div
          className={cn(
            styles.doubleIconContainer,
            isLowerJaw && styles.lowerJaw,
          )}
        >
          <ToothChartIcon
            className={styles.doubleIcon}
            status={status}
            ISONumber={ISONumber}
            blackAndWhite={blackAndWhite}
          />

          <ToothChartIcon
            className={styles.doubleIcon}
            status={status}
            ISONumber={ISONumber}
            blackAndWhite={blackAndWhite}
          />
        </div>
      ) : (
        <ToothChartIcon
          status={isRemoved ? ToothStatus.notFound : status}
          ISONumber={ISONumber}
          blackAndWhite={blackAndWhite}
        />
      )}
      <span className={styles.number}>
        {getDisplayToothNumber(ISONumber, dentalNotationFormat)}

        {(isSuper || Boolean(supernumeraryIndex)) && '*'}
      </span>
    </div>
  );
};

const InternalToothChartItemWithRef = React.forwardRef<
  HTMLDivElement,
  ToothChartItemProps
>(InternalToothChartItem);

export const ToothChartItem = React.memo(InternalToothChartItemWithRef);
